/* =========================================================================
   ADORN: header search popdown - SEARCH-009 / SEARCH-010

   Loaded after assets/adorn-search-fix.js, which owns SEARCH-001/003/005/006/
   007/008. Kept in its own file so the two can be rolled back independently.

   SEARCH-009  the popdown can no longer wedge open
   SEARCH-010  opening search does not freeze the page on desktop

   SEARCH-009
   ----------
   Two class names in theme.js each block the other from clearing, and the pair
   deadlocks the moment a customer opens search and then reaches for the menu.

   SearchPopdown.open() sets body.search-opened and fires theme:scroll:lock.
   Its outside-click handler is:

       onBodyClick(t){
         const i = this.contains(t.target);
         (this.header?.classList.contains("site-header--menu-opened") || i || i)
           || this.close()
       }

   ...so while the header carries site-header--menu-opened, NO outside click
   closes the search. HoverDisclosure.showDisclosure() adds exactly that class
   on the first nav hover.

   Going the other way, the header's own updateBackgroundHeight() refuses to
   drop its hovered/menu state while search-opened is set:

       a = document.body.classList.contains("search-opened"),
       r = this.classList.contains("site-header--menu-opened");
       a || r || (this.classList.remove("site-header--hovered"), ...)

   Each flag is waiting on the other. Once both are set the search panel is
   stuck open, the page stays scroll-locked, and neither an outside click nor
   Escape gets out - only the X or the search icon does. The top-level CATEGORY
   link is href="#", so clicking it cannot navigate out of the state either.

   Verified live 2026-08-29: with the nav untouched, an outside click closes the
   search and body overflow returns to visible; after one megamenu hover it does
   not.

   Fix: close the search BEFORE the megamenu opens, and stop depending on the
   theme's guarded outside-click path.

   SEARCH-010
   ----------
   open() fires theme:scroll:lock unconditionally. That is right for the mobile
   full-screen search; on desktop the popdown is a 66px bar under the header, and
   locking the whole document behind it means a customer who opens search and
   looks away finds a page that will not scroll. Desktop unlocks immediately.

   Rollback: delete this file and its tag in layout/theme.liquid.
   ========================================================================= */
(function () {
  'use strict';

  var SMALL_BREAKPOINT = (window.theme && window.theme.sizes && window.theme.sizes.small) || 768;
  var TEARDOWN_GRACE = 450;
  var UNLOCK_ATTEMPTS = [0, 60, 250, 600];

  /* Bumped on every open and every close. A deferred backstop carries the value
     it was scheduled with and gives up if the popdown has moved on since - so a
     customer who reopens search inside the grace window below is never closed by
     the previous close's cleanup. */
  var stateSeq = new WeakMap();

  function bump(popdown) {
    var next = (stateSeq.get(popdown) || 0) + 1;
    stateSeq.set(popdown, next);
    return next;
  }

  function headerPopdowns() {
    return document.querySelectorAll('search-popdown[data-popdown-in-header]');
  }

  function isOpen(popdown) {
    var details = popdown.querySelector('details');
    return !!(details && details.hasAttribute('open'));
  }

  /* Call the theme's own close() - that alone is enough to sidestep SEARCH-009,
     because the deadlock is in the guard on onBodyClick, not in close() itself.
     Let the theme run its normal teardown from there (close -> transitionend ->
     popdownTransitionCallback -> onClose), and only step in if it has not
     finished, which is what happens when the transition never fires: reduced
     motion, display:none mid-animation, a backgrounded tab.

     Calling onClose() eagerly instead of waiting was the first version of this,
     and it broke reopening - the theme's own teardown then arrived late, after
     the customer had clicked the icon again, and closed the fresh popdown. Every
     second click on the search icon did nothing. Hence the sequence guard. */
  function forceCloseSearch(popdown) {
    if (!popdown || !isOpen(popdown)) return;

    var details = popdown.querySelector('details');
    var seq = bump(popdown);

    try { if (typeof popdown.close === 'function') popdown.close(); } catch (e) { /* older bundle */ }

    window.setTimeout(function () {
      if (stateSeq.get(popdown) !== seq) return;      /* reopened since - leave it */
      if (!details.hasAttribute('open')) return;      /* theme finished on its own */

      try { if (typeof popdown.onClose === 'function') popdown.onClose(); } catch (e) { /* older bundle */ }

      details.removeAttribute('open');
      details.classList.remove('is-open');
      document.body.classList.remove('search-opened');
      document.dispatchEvent(new CustomEvent('theme:search:close', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }, TEARDOWN_GRACE);
  }

  function closeAllHeaderSearch() {
    var popdowns = headerPopdowns();
    for (var i = 0; i < popdowns.length; i++) forceCloseSearch(popdowns[i]);
  }

  /* ----------------------------------------------------------------
     Trigger 1 - the megamenu is about to open

     mouseenter does not bubble, so this is a capture-phase listener on document:
     capture runs root -> target for every event, which reaches every
     hover-disclosure without binding to each one, including any added later.
     Closing here means the search is already gone before showDisclosure() sets
     site-header--menu-opened, so the deadlock never forms.
     ---------------------------------------------------------------- */
  ['mouseenter', 'pointerenter', 'focusin'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        if (!event.target || !event.target.closest) return;
        if (!event.target.closest('hover-disclosure')) return;
        closeAllHeaderSearch();
      },
      true
    );
  });

  /* Trigger 2 - belt and braces. If the class lands by some route that does not
     pass through a hover-disclosure (keyboard, touch, a future section), catch
     it on the header's class attribute and close then. */
  function watchHeader() {
    var header = document.querySelector('header-component');
    if (!header || header.hasAttribute('data-adorn-search-deadlock-watched')) return;
    header.setAttribute('data-adorn-search-deadlock-watched', '');

    new MutationObserver(function () {
      if (header.classList.contains('site-header--menu-opened')) closeAllHeaderSearch();
    }).observe(header, { attributes: true, attributeFilter: ['class'] });
  }

  /* ----------------------------------------------------------------
     Trigger 3 - outside click, without the theme's guard

     Replaces onBodyClick's site-header--menu-opened bail-out. Clicks inside the
     popdown (which includes the summary toggle) are left alone so opening and
     the X keep working; everything else closes.
     ---------------------------------------------------------------- */
  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest) return;
      if (event.target.closest('search-popdown')) return;
      closeAllHeaderSearch();
    },
    true
  );

  /* Trigger 4 - Escape. The theme's own Escape path runs through the same
     guarded close, so it dies in the deadlock too. */
  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Escape' && event.keyCode !== 27) return;
      closeAllHeaderSearch();
    },
    true
  );

  /* ----------------------------------------------------------------
     SEARCH-010 - desktop keeps its scroll

     open() dispatches theme:scroll:lock synchronously, but the theme's handler
     applies it inside a bare setTimeout:

         function E(e){ setTimeout(()=>{ ... disablePageScroll(e.detail) ... }) }

     so the frame on which the lock actually lands is not deterministic. A single
     unlock fired a couple of frames after the toggle wins that race sometimes
     and loses it others - measured here as the lock sticking on roughly half of
     opens, which is worse than never unlocking at all because it is not
     reproducible. So assert the unlocked state a few times across the window in
     which the lock can arrive, and only actually dispatch while the lock is on.
     data-scroll-locked is set by the same handler, so it is the honest signal.

     'toggle' does not bubble; same capture-phase trick as Trigger 1. Only the
     header popdowns are touched - the search RESULTS page renders its own
     in-page popdown that was never scroll-locking anything.
     ---------------------------------------------------------------- */
  function isDesktop() {
    return window.innerWidth >= SMALL_BREAKPOINT && matchMedia('(pointer:fine)').matches;
  }

  function keepDesktopScrollable(popdown) {
    UNLOCK_ATTEMPTS.forEach(function (delay) {
      window.setTimeout(function () {
        if (!isOpen(popdown) || !isDesktop()) return;
        if (!document.documentElement.hasAttribute('data-scroll-locked')) return;
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      }, delay);
    });
  }

  document.addEventListener(
    'toggle',
    function (event) {
      var popdown = event.target && event.target.closest && event.target.closest('search-popdown');
      if (!popdown || !popdown.hasAttribute('data-popdown-in-header')) return;
      if (!isOpen(popdown)) return;

      /* An open supersedes any pending close backstop for this popdown. */
      bump(popdown);

      if (isDesktop()) keepDesktopScrollable(popdown);
    },
    true
  );

  /* A resize across the breakpoint must not strand a lock either way. */
  window.addEventListener('resize', function () {
    var popdowns = headerPopdowns();
    for (var i = 0; i < popdowns.length; i++) {
      if (isOpen(popdowns[i])) keepDesktopScrollable(popdowns[i]);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchHeader);
  } else {
    watchHeader();
  }
})();
